import { ReactNode } from 'react';

import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import { SignIn, SignUp, Invite, Organization, Expired } from '@pages';

import { Loading } from '@components';

import ScrollToTop from '@app/routes/ScrollToTop';
import { useAppSelector } from '@store/redux/store';
import { extractFrom, setFrom } from '@utils/redirect';
import { NavigationRoutes } from './navigation.routes';

import { Auth0ProviderWithHistory } from '../auth0';
import RouterContextApp from './routerContext';

interface IPrivateRoute {
  children: ReactNode;
  path: string;
}

// Todo: use withAuthRequired hoc.
const PrivateRoute = ({ children, ...rest }: IPrivateRoute) => {
  const { isAuthenticated, isLoading } = useAuth0();

  const { organization } = useAppSelector();
  const todayDate = new Date();
  const location = useLocation();
  if (isLoading) {
    return <Loading />;
  }
  const redirect = extractFrom(location);
  const doesntNeedRedirect = isAuthenticated || redirect;
  let params: URLSearchParams = new URLSearchParams(location.search); // important to add location.search, else infinite loop.

  if (!doesntNeedRedirect) {
    params = setFrom(redirect);
  }

  if (
    isAuthenticated &&
    organization.expirationDateTime &&
    todayDate.getTime() > new Date(organization.expirationDateTime).getTime()
  ) {
    return <Redirect to="/expired" />;
  }

  return (
    <Route
      {...rest}
      render={() =>
        isAuthenticated ? (
          children
        ) : (
          <Redirect to={`/login?${params.toString()}`} />
        )
      }
    />
  );
};

interface IAuthRoute {
  children: ReactNode;
  path: string;
}

function AuthRoute({ children, ...rest }: IAuthRoute): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();

  if (isLoading) {
    return <Loading />;
  }
  const redirect = extractFrom(location);
  const needsRedirect = isAuthenticated && redirect;
  const pathname = needsRedirect ? redirect : '/products';

  return (
    <Route
      {...rest}
      render={() => (!isAuthenticated ? children : <Redirect to={pathname} />)}
    />
  );
}

const Routes = () => {
  return (
    <Router>
      <RouterContextApp>
        <>
          <ScrollToTop />
          <Switch>
            <Route path="/invite/:id">
              <Invite />
            </Route>
            <Auth0ProviderWithHistory>
              <Route path="/expired">
                <Expired />
              </Route>
              <AuthRoute path="/signin">
                <SignIn />
              </AuthRoute>
              <AuthRoute path="/login">
                <Organization />
              </AuthRoute>
              <AuthRoute path="/signup">
                <SignUp />
              </AuthRoute>
              <PrivateRoute path="*">
                <NavigationRoutes />
              </PrivateRoute>
            </Auth0ProviderWithHistory>
          </Switch>
        </>
      </RouterContextApp>
    </Router>
  );
};

export { Routes };
